import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n/index.jsx';
import { toHtml, PALETTE } from '../richText.js';
import { cleanPaste } from './pasteClean.js';
import { hydrateImages, cleanHtml, uploadDocImage, imageHtml, tableHtml, tableOp, startColumnDrag, nearColumnEdge } from './docTools.js';
import { BLOCK_KINDS, blockElement, blockInner, hydrateBlocks, readBlock } from './docBlocks.js';

// Block dialogs (documents only): loaded when first used.
const DocBlockEditor = lazy(() => import('./DocBlockEditor.jsx'));
const CatalogueImport = lazy(() => import('./DocBlockEditor.jsx').then((m) => ({ default: m.CatalogueImport })));

// Rich-text box. Simple (listings): bold, italic, bulleted and numbered
// lists. Full (website pages, news): a small "Word" toolbar — paragraph
// style, headings, bold / italic / underline, highlight, brand colours,
// size, alignment, lists, quote, link, divider, image (when allowed),
// clear formatting; pasting from Word / Google Docs keeps the structure
// and drops their fonts and styles. Formatting is stored as fixed classes;
// the server keeps only those (rich.js), never inline styles.
// Documents (docs): also tables, pictures (private), indent, line spacing,
// page breaks — see docTools.js — and blocks (+ Bloc: technical table,
// picture + details, picture row, two columns, from the catalogue) — see
// docBlocks.js.
const TOOLS = [
  { cmd: 'bold', label: 'B', key: 'bold', cls: 'rt-bold' },
  { cmd: 'italic', label: 'I', key: 'italic', cls: 'rt-italic' },
  { cmd: 'insertUnorderedList', label: '•', key: 'bullets' },
  { cmd: 'insertOrderedList', label: '1.', key: 'numbers' },
];

export const htmlText = (html) => {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return (d.textContent || '').trim();
};

const BLOCK_STYLES = ['normal', 'lead', 'note', 'box', 'quote', 'cols', 'check'];
const SIZES = ['sm', 'normal', 'lg', 'xl'];
const ALIGNS = [['left', 'M3 6h18M3 12h12M3 18h16'], ['center', 'M3 6h18M6 12h12M4 18h16'], ['right', 'M3 6h18M9 12h12M5 18h16'], ['justify', 'M3 6h18M3 12h18M3 18h18']];
const BLOCK_SEL = 'p,h2,h3,blockquote,li';
const TABLE_SIZES = [[2, 2], [3, 3], [4, 4], [4, 6], [5, 8]];
const TABLE_OPS = ['rowAbove', 'rowBelow', 'colLeft', 'colRight', 'delRow', 'delCol', 'mergeRight', 'mergeDown', 'split', 'header', 'align-left', 'align-center', 'align-right', 'cellShade', 'borders', 'shade', 'delete'];
const LINE_SPACING = [['lh-1', '1'], ['', '1,25'], ['lh-15', '1,5'], ['lh-2', '2']];

// ── DOM helpers (selection inside the editable box) ─────────────────────
function rangeIn(box) {
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  const r = sel.getRangeAt(0);
  return box.contains(r.commonAncestorContainer) ? r : null;
}
function blocksIn(box, r) {
  const list = [...box.querySelectorAll(BLOCK_SEL)].filter((b) => r.intersectsNode(b) && !b.querySelector(BLOCK_SEL));
  if (list.length) return list;
  const n = r.startContainer.nodeType === 1 ? r.startContainer : r.startContainer.parentElement;
  const b = n?.closest(BLOCK_SEL);
  return b && box.contains(b) ? [b] : [];
}
function replaceTag(el, tag) {
  if (el.tagName.toLowerCase() === tag) return el;
  const n = document.createElement(tag);
  for (const c of el.classList) if (c.startsWith('ta-')) n.classList.add(c);
  while (el.firstChild) n.appendChild(el.firstChild);
  el.replaceWith(n);
  return n;
}
const unwrap = (el) => { const p = el.parentNode; while (el.firstChild) p.insertBefore(el.firstChild, el); el.remove(); };
// Wraps each selected piece of text in a new element (works across paragraphs).
function wrapSelection(box, make, clearSel) {
  const r = rangeIn(box);
  if (!r || r.collapsed) return false;
  const root = r.commonAncestorContainer.nodeType === 1 ? r.commonAncestorContainer : r.commonAncestorContainer.parentNode;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) if (r.intersectsNode(walker.currentNode) && walker.currentNode.data.length) nodes.push(walker.currentNode);
  const made = [];
  for (const n of nodes) {
    const start = n === r.startContainer ? r.startOffset : 0;
    const end = n === r.endContainer ? r.endOffset : n.length;
    if (start >= end) continue;
    let t = n;
    if (end < t.length) t.splitText(end);
    if (start > 0) t = t.splitText(start);
    if (clearSel) {
      // Same kind of formatting already around this text: replaced, not nested.
      let a = t.parentElement;
      while (a && a !== box) { const up = a.parentElement; if (a.matches(clearSel) && a.textContent === t.data) unwrap(a); a = up; }
    }
    const el = make();
    t.parentNode.insertBefore(el, t);
    el.appendChild(t);
    made.push(el);
  }
  if (made.length) {
    const sel = window.getSelection();
    const nr = document.createRange();
    nr.setStartBefore(made[0]); nr.setEndAfter(made[made.length - 1]);
    sel.removeAllRanges(); sel.addRange(nr);
  }
  return made.length > 0;
}
// Removes a kind of inline formatting around / inside the selection.
function unwrapIn(box, selector) {
  const r = rangeIn(box);
  if (!r) return;
  for (const el of [...box.querySelectorAll(selector)]) if (r.intersectsNode(el)) unwrap(el);
}

function Menu({ label, title, items, onPick, className = '' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <span className={`rt-menu ${className}`} ref={ref}>
      <button type="button" title={title} aria-label={title} aria-haspopup="true" aria-expanded={open} onMouseDown={(e) => e.preventDefault()} onClick={() => setOpen((o) => !o)}>{label} <span className="rt-caret" aria-hidden="true">▾</span></button>
      {open && (
        <span className="rt-menu-list" role="menu">
          {items.map((it) => (
            <button key={it.key} type="button" role="menuitem" className={it.cls} onMouseDown={(e) => e.preventDefault()} onClick={() => { setOpen(false); onPick(it.key); }}>{it.content}</button>
          ))}
        </span>
      )}
    </span>
  );
}

export default function RichTextEditor({ id, value, onChange, placeholder, invalid, describedBy, full = false, onPickImage, disabled, docs = false, onError, docLang, blocks = true }) {
  const { t } = useI18n();
  const box = useRef(null);
  const last = useRef(null);
  const fileRef = useRef(null);
  const [selImg, setSelImg] = useState(null); // the picture being edited (documents)
  const [imgBar, setImgBar] = useState(null);  // its toolbar position
  const [selBlock, setSelBlock] = useState(null); // the block clicked (documents)
  const [blockBar, setBlockBar] = useState(null);
  const [blockEdit, setBlockEdit] = useState(null); // { block, el } — el null = new
  const [importing, setImporting] = useState(false);
  // Stable: the dialog (ws Modal) re-runs its focus effect when onClose changes.
  const closeBlock = useCallback(() => setBlockEdit(null), []);
  const closeImport = useCallback(() => setImporting(false), []);

  // Only write into the box when the value changed from outside (loading
  // a listing), never while typing — that would move the caret. Older
  // plain text arrives as paragraphs.
  useEffect(() => {
    if (box.current && value !== last.current) {
      box.current.innerHTML = full ? toHtml(value) : (value || '');
      last.current = value;
      if (docs) {
        hydrateBlocks(box.current, { editable: true });
        hydrateImages(box.current);
        // A line to type in before / after a block that starts or ends the text.
        const line = () => Object.assign(document.createElement('p'), { innerHTML: '<br>' });
        if (box.current.lastElementChild?.classList.contains('dblock')) box.current.appendChild(line());
        if (box.current.firstElementChild?.classList.contains('dblock')) box.current.prepend(line());
      }
    }
  }, [value, full, docs]);

  // Browsers sometimes nest blocks (a list inside a paragraph) or leave
  // empty class attributes: tidied before saving.
  const tidy = () => {
    if (!full) return;
    for (const p of [...box.current.querySelectorAll('p')]) {
      if (p.querySelector('ul, ol, p, h2, h3, blockquote')) unwrap(p);
    }
    for (const el of [...box.current.querySelectorAll('[class=""]')]) el.removeAttribute('class');
  };
  // Typing: as is. Toolbar actions and leaving the box: tidied.
  const emit = (clean = true) => {
    if (clean) tidy();
    const html = docs ? cleanHtml(box.current) : box.current.innerHTML;
    last.current = html;
    onChange(html);
  };
  // Remember the caret before a dialog (link / image picker) takes focus.
  const saved = useRef(null);
  const keepSelection = () => {
    const sel = window.getSelection();
    if (sel?.rangeCount && box.current.contains(sel.anchorNode)) saved.current = sel.getRangeAt(0).cloneRange();
  };
  const restoreSelection = () => {
    box.current.focus();
    const sel = window.getSelection();
    if (saved.current && sel) { sel.removeAllRanges(); sel.addRange(saved.current); }
  };
  const exec = (cmd, arg = null) => {
    box.current.focus();
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* older browsers */ }
    try { document.execCommand('styleWithCSS', false, false); } catch { /* older browsers */ }
    document.execCommand(cmd, false, arg);
  };
  // Text typed straight into the box (no paragraph yet) gets one first.
  const ensureBlock = () => {
    const r = rangeIn(box.current);
    if (r && !blocksIn(box.current, r).length) exec('formatBlock', '<p>');
  };
  const run = async (tool) => {
    if (typeof tool === 'string') tool = { cmd: tool };
    keepSelection();
    if (tool.cmd === 'link') {
      const url = window.prompt(t('listingForm.rt.linkPrompt'), 'https://');
      restoreSelection();
      if (!url || url === 'https://') return;
      if (!/^(https?:\/\/|mailto:|\/)/i.test(url.trim())) return;
      document.execCommand('createLink', false, url.trim());
      emit();
      return;
    }
    if (tool.cmd === 'image') {
      const img = onPickImage ? await onPickImage() : null;
      restoreSelection();
      if (!img) return;
      document.execCommand('insertHTML', false, `<img src="${img.url}" alt="${(img.alt || '').replace(/"/g, '&quot;')}">`);
      emit();
      return;
    }
    exec(tool.cmd, tool.arg ? `<${tool.arg}>` : null);
    emit();
  };

  // ── Full toolbar actions ──
  const inline = (make, clearSel) => { box.current.focus(); if (wrapSelection(box.current, make, clearSel)) emit(); };
  const color = (k) => {
    if (k === 'default') { unwrapIn(box.current, 'span[class*="tc-"]'); emit(); return; }
    inline(() => Object.assign(document.createElement('span'), { className: `tc-${k}` }), 'span[class*="tc-"]');
  };
  const size = (k) => {
    if (k === 'normal') { unwrapIn(box.current, 'span[class*="fs-"]'); emit(); return; }
    inline(() => Object.assign(document.createElement('span'), { className: `fs-${k}` }), 'span[class*="fs-"]');
  };
  const highlight = (k) => {
    if (k === 'none') { unwrapIn(box.current, 'mark'); emit(); return; }
    inline(() => Object.assign(document.createElement('mark'), { className: `hl-${k}` }), 'mark');
  };
  const align = (k) => {
    box.current.focus();
    ensureBlock();
    const r = rangeIn(box.current);
    if (!r) return;
    for (const b of blocksIn(box.current, r)) {
      b.classList.remove('ta-left', 'ta-center', 'ta-right', 'ta-justify');
      if (k !== 'left') b.classList.add(`ta-${k}`);
      if (!b.classList.length) b.removeAttribute('class');
    }
    emit();
  };
  const blockStyle = (k) => {
    box.current.focus();
    ensureBlock();
    let r = rangeIn(box.current);
    if (!r) return;
    if (k === 'check') {
      if (!blocksIn(box.current, r).some((b) => b.closest('ul'))) exec('insertUnorderedList');
      r = rangeIn(box.current);
      for (const b of blocksIn(box.current, r)) b.closest('ul')?.classList.add('bs-check');
      emit();
      return;
    }
    if (k === 'quote') exec('formatBlock', '<blockquote>');
    r = rangeIn(box.current);
    for (let b of blocksIn(box.current, r)) {
      if (b.tagName === 'LI') { if (k === 'normal') b.closest('ul')?.classList.remove('bs-check'); continue; }
      if (k === 'quote') { b = b.closest('blockquote') || replaceTag(b, 'blockquote'); }
      else if (b.tagName === 'BLOCKQUOTE' || k !== 'normal') b = replaceTag(b, 'p');
      for (const c of [...b.classList]) if (c.startsWith('bs-')) b.classList.remove(c);
      const cls = { lead: 'bs-lead', note: 'bs-note', box: 'bs-box', cols: 'bs-cols', quote: 'bs-quote' }[k];
      if (cls) b.classList.add(cls);
      if (!b.classList.length) b.removeAttribute('class');
    }
    emit();
  };
  const clear = () => {
    box.current.focus();
    exec('removeFormat');
    unwrapIn(box.current, 'span, mark, u, b, strong, i, em, font');
    const r = rangeIn(box.current);
    if (r) {
      for (const b of blocksIn(box.current, r)) {
        if (b.tagName === 'LI') { b.removeAttribute('class'); b.closest('ul')?.classList.remove('bs-check'); continue; }
        replaceTag(b, 'p').removeAttribute('class');
      }
    }
    emit();
  };
  // Documents: a picture inserted where the caret is (button, paste, drop).
  const insertPictures = async (files) => {
    const imgs = [...files].filter((f) => /^image\/(png|jpe?g|webp|gif|bmp)$/i.test(f.type));
    for (const f of imgs) {
      if (f.size > 25 * 1024 * 1024) { onError?.('image_too_large'); continue; }
      try {
        const r = await uploadDocImage(f);
        restoreSelection();
        document.execCommand('insertHTML', false, imageHtml(r.id));
        hydrateImages(box.current);
        emit();
      } catch (err) { onError?.(err.code || 'server_error'); }
    }
    return imgs.length > 0;
  };
  const paste = (e) => {
    if (docs && e.clipboardData.files?.length && [...e.clipboardData.files].some((f) => f.type.startsWith('image/'))) {
      e.preventDefault(); keepSelection(); insertPictures(e.clipboardData.files); return;
    }
    e.preventDefault();
    const html = full ? e.clipboardData.getData('text/html') : '';
    if (html) {
      const clean = cleanPaste(html, { tables: docs });
      if (clean) { document.execCommand('insertHTML', false, clean); emit(); return; }
    }
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  };

  // ── Documents: tables, pictures, indent, line spacing, page break ──
  const insertTable = (k) => {
    let [cols, rows] = String(k).split('x').map(Number);
    if (k === 'custom') {
      const v = window.prompt(t('listingForm.rt.tablePrompt'), '4 x 3');
      const m = String(v || '').match(/(\d+)\s*[x×*]\s*(\d+)/i);
      if (!m) return;
      cols = Math.min(10, Number(m[1])); rows = Math.min(60, Number(m[2]));
    }
    box.current.focus(); ensureBlock();
    document.execCommand('insertHTML', false, tableHtml(rows, cols, true));
    emit();
  };
  const tableAction = (op) => { box.current.focus(); if (tableOp(box.current, op)) emit(); else onError?.('rt_not_in_table'); };
  const indent = (d) => {
    box.current.focus(); ensureBlock();
    const r = rangeIn(box.current);
    if (!r) return;
    for (const b0 of blocksIn(box.current, r)) {
      const b = b0.tagName === 'LI' ? b0.closest('ul, ol') : b0;
      const cur = Number(([...b.classList].find((c) => /^ind-\d$/.test(c)) || 'ind-0').slice(4));
      b.classList.remove(`ind-${cur}`);
      const n = Math.max(0, Math.min(4, cur + d));
      if (n) b.classList.add(`ind-${n}`);
      if (!b.classList.length) b.removeAttribute('class');
    }
    emit();
  };
  const spacing = (k) => {
    box.current.focus(); ensureBlock();
    const r = rangeIn(box.current);
    if (!r) return;
    for (const b of blocksIn(box.current, r)) { b.classList.remove('lh-1', 'lh-15', 'lh-2'); if (k) b.classList.add(k); if (!b.classList.length) b.removeAttribute('class'); }
    emit();
  };
  const pageBreak = () => { box.current.focus(); document.execCommand('insertHTML', false, '<hr class="page-break"><p><br></p>'); emit(); };
  // A picture clicked: its toolbar (size, alignment, caption, remove).
  const pickImg = (img) => {
    for (const x of box.current.querySelectorAll('img.is-selected')) x.classList.remove('is-selected');
    if (!img) { setSelImg(null); setImgBar(null); return; }
    img.classList.add('is-selected');
    const b = box.current.getBoundingClientRect(); const r = img.getBoundingClientRect();
    setSelImg(img); setImgBar({ top: r.top - b.top + box.current.offsetTop - 40, left: Math.max(0, r.left - b.left) });
  };
  const imgSet = (fn) => { if (!selImg) return; fn(selImg); emit(); pickImg(selImg.isConnected ? selImg : null); };
  const imgAlign = (k) => imgSet((img) => {
    img.classList.remove('im-left', 'im-center', 'im-right'); img.classList.add(`im-${k}`);
    const holder = img.closest('figure') || img.parentElement;
    if (holder && holder !== box.current) {
      holder.classList.remove('ta-left', 'ta-center', 'ta-right', 'im-left', 'im-center', 'im-right');
      holder.classList.add(holder.tagName === 'FIGURE' ? `im-${k}` : `ta-${k}`);
    }
  });
  // Caption on / off: a figure with an editable caption, or back to a paragraph.
  const imgCaption = () => imgSet((img) => {
    const fig = img.closest('figure');
    const k = (([...img.classList].find((c) => c.startsWith('im-'))) || 'im-center').slice(3);
    if (fig) { const p = document.createElement('p'); p.className = `ta-${k}`; p.appendChild(img); fig.replaceWith(p); return; }
    const f = document.createElement('figure');
    f.className = `im-${k}`;
    const p = img.parentElement !== box.current && img.parentElement.tagName === 'P' && img.parentElement.childNodes.length === 1 ? img.parentElement : null;
    (p || img).replaceWith(f);
    f.appendChild(img);
    f.appendChild(Object.assign(document.createElement('figcaption'), { textContent: t('listingForm.rt.captionPh') }));
  });
  const imgRemove = () => { if (!selImg) return; const fig = selImg.closest('figure'); (fig || selImg).remove(); pickImg(null); emit(); };

  // ── Blocks (documents) ──
  const pickBlock = (el) => {
    for (const x of box.current.querySelectorAll('.dblock.is-selected')) x.classList.remove('is-selected');
    if (!el) { setSelBlock(null); setBlockBar(null); return; }
    el.classList.add('is-selected');
    const b = box.current.getBoundingClientRect(); const r = el.getBoundingClientRect();
    setSelBlock(el); setBlockBar({ top: r.top - b.top + box.current.offsetTop - 40, left: 0 });
  };
  const after = useRef(null);
  // The top-level element holding the caret: blocks go after it.
  const anchorOf = () => {
    let n = saved.current?.startContainer;
    if (!n || !box.current.contains(n)) return null;
    while (n && n.parentNode !== box.current) n = n.parentNode;
    return n && n !== box.current ? n : null;
  };
  const insertBlocks = (list) => {
    let at = after.current?.isConnected ? after.current : anchorOf();
    after.current = null;
    // An empty paragraph where the caret is: the block takes its place.
    const empty = at && at.nodeType === 1 && at.tagName === 'P' && !at.textContent.trim() && !at.querySelector('img') ? at : null;
    for (const b of list) {
      const el = blockElement(b);
      if (empty && at === empty) { empty.replaceWith(el); } else if (at) at.after(el); else box.current.appendChild(el);
      at = el;
    }
    // Somewhere to keep typing after the last block.
    if (at && !at.nextElementSibling) at.after(Object.assign(document.createElement('p'), { innerHTML: '<br>' }));
    hydrateImages(box.current);
    emit();
    pickBlock(null);
  };
  const openBlock = (kind) => {
    keepSelection();
    // A block selected: the new one goes right after it.
    after.current = selBlock?.isConnected ? selBlock : null;
    if (kind === 'catalogue') { setImporting(true); return; }
    setBlockEdit({ block: { kind }, el: null });
  };
  const saveBlock = (b) => {
    const el = blockEdit?.el;
    setBlockEdit(null);
    if (!el) { insertBlocks([b]); return; }
    el.dataset.block = JSON.stringify(b);
    el.innerHTML = blockInner(b);
    hydrateImages(box.current);
    emit();
    pickBlock(el);
  };
  const blockAct = (op) => {
    const el = selBlock;
    if (!el?.isConnected) { pickBlock(null); return; }
    if (op === 'edit') { setBlockEdit({ block: readBlock(el), el }); return; }
    if (op === 'up' && el.previousElementSibling) el.previousElementSibling.before(el);
    if (op === 'down' && el.nextElementSibling) el.nextElementSibling.after(el);
    if (op === 'copy') { const c = blockElement(readBlock(el)); el.after(c); hydrateImages(box.current); emit(); pickBlock(c); return; }
    if (op === 'delete') { el.remove(); emit(); pickBlock(null); return; }
    emit();
    pickBlock(el);
  };

  const sw = (k) => <span className="rt-swatch" style={{ background: PALETTE[k] }} aria-hidden="true" />;
  const btn = (key, label, onClick, cls = '') => (
    <button key={key} type="button" className={cls} title={t(`listingForm.rt.${key}`)} aria-label={t(`listingForm.rt.${key}`)} disabled={disabled}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick}>{label}</button>
  );
  const icon = (d) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d={d} /></svg>;

  return (
    <div className={`rt${invalid ? ' is-invalid' : ''}${full ? ' rt-full' : ''}${disabled ? ' is-disabled' : ''}`}>
      <div className="rt-toolbar" role="toolbar" aria-label={t('listingForm.rt.toolbar')}>
        {!full && TOOLS.map((tool) => btn(tool.key, tool.label, () => run(tool), tool.cls))}
        {full && (
          <>
            <span className="rt-group">
              <Menu label={t('listingForm.rt.style')} title={t('listingForm.rt.styleTitle')} className="rt-menu-wide" onPick={blockStyle}
                items={BLOCK_STYLES.map((k) => ({ key: k, cls: `rt-style-item is-${k}`, content: t(`listingForm.rt.styles.${k}`) }))} />
              {btn('h2', 'H2', () => run({ cmd: 'formatBlock', arg: 'h2' }))}
              {btn('h3', 'H3', () => run({ cmd: 'formatBlock', arg: 'h3' }))}
              {btn('paragraph', '¶', () => run({ cmd: 'formatBlock', arg: 'p' }))}
            </span>
            <span className="rt-group">
              {btn('bold', 'B', () => run('bold'), 'rt-bold')}
              {btn('italic', 'I', () => run('italic'), 'rt-italic')}
              {btn('underline', 'U', () => run('underline'), 'rt-underline')}
              <Menu label={<span className="rt-hl-icon">A</span>} title={t('listingForm.rt.highlight')} onPick={highlight}
                items={[{ key: 'gold', content: <><mark className="hl-gold">Aa</mark> {t('listingForm.rt.hl.gold')}</> }, { key: 'cream', content: <><mark className="hl-cream">Aa</mark> {t('listingForm.rt.hl.cream')}</> }, { key: 'none', content: t('listingForm.rt.hl.none') }]} />
              <Menu label={<span className="rt-color-icon">A</span>} title={t('listingForm.rt.color')} onPick={color}
                items={[...Object.keys(PALETTE).map((k) => ({ key: k, content: <>{sw(k)} {t(`listingForm.rt.colors.${k}`)}</> })), { key: 'default', content: t('listingForm.rt.colors.default') }]} />
              <Menu label={t('listingForm.rt.sizeShort')} title={t('listingForm.rt.size')} onPick={size}
                items={SIZES.map((k) => ({ key: k, content: <span className={k === 'normal' ? '' : `fs-${k}`}>{t(`listingForm.rt.sizes.${k}`)}</span> }))} />
            </span>
            <span className="rt-group">
              {ALIGNS.map(([k, d]) => btn(`align_${k}`, icon(d), () => align(k)))}
            </span>
            <span className="rt-group">
              {btn('bullets', '•', () => run('insertUnorderedList'))}
              {btn('numbers', '1.', () => run('insertOrderedList'))}
              {btn('quote', '❝', () => run({ cmd: 'formatBlock', arg: 'blockquote' }))}
              {btn('link', '🔗', () => run('link'))}
              {btn('unlink', '⛓', () => run('unlink'))}
              {btn('divider', '—', () => run('insertHorizontalRule'))}
              {onPickImage && btn('image', '🖼', () => run('image'))}
              {btn('clear', icon('M4 7V5h11v2M9.5 5l-3 14M14 19l7-7M21 19l-7-7'), clear)}
            </span>
            {docs && (
              <span className="rt-group">
                <Menu label="▦" title={t('listingForm.rt.table')} className="rt-menu-wide rt-menu-table" onPick={(k) => (k.startsWith('op:') ? tableAction(k.slice(3)) : insertTable(k))}
                  items={[...TABLE_SIZES.map(([c, r]) => ({ key: `${c}x${r}`, content: t('listingForm.rt.tableInsert', { c, r }) })), { key: 'custom', content: t('listingForm.rt.tableCustom') },
                    ...TABLE_OPS.map((op) => ({ key: `op:${op}`, cls: op === 'rowAbove' ? 'rt-sep' : '', content: t(`listingForm.rt.tableOps.${op}`) }))]} />
                {btn('picture', '🖼', () => { keepSelection(); fileRef.current?.click(); })}
                {btn('outdent', '⇤', () => indent(-1))}
                {btn('indent', '⇥', () => indent(1))}
                <Menu label="↕" title={t('listingForm.rt.lineSpacing')} onPick={(k) => spacing(k === 'normal' ? '' : k)} items={LINE_SPACING.map(([k, v]) => ({ key: k || 'normal', content: v }))} />
                {btn('pageBreak', '⤓', pageBreak)}
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => { insertPictures(e.target.files); e.target.value = ''; }} />
              </span>
            )}
            {docs && blocks && (
              <span className="rt-group">
                <Menu label={`+ ${t('docBlocks.block')}`} title={t('docBlocks.insertBlock')} className="rt-menu-wide rt-menu-block" onPick={openBlock}
                  items={[...BLOCK_KINDS, 'catalogue'].map((k) => ({ key: k, cls: k === 'catalogue' ? 'rt-sep' : '', content: t(`docBlocks.kind.${k}`) }))} />
              </span>
            )}
          </>
        )}
      </div>
      <div
        id={id}
        ref={box}
        className={`rt-area${full ? ' rich-text' : ''}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        data-placeholder={placeholder}
        onInput={() => emit(false)}
        onBlur={() => emit()}
        onPaste={paste}
        onClick={docs ? (e) => {
          const blk = e.target.closest?.('.dblock');
          pickBlock(blk && box.current.contains(blk) ? blk : null);
          pickImg(!blk && e.target.tagName === 'IMG' ? e.target : null);
        } : undefined}
        onDoubleClick={docs ? (e) => { const blk = e.target.closest?.('.dblock'); if (blk) setBlockEdit({ block: readBlock(blk), el: blk }); } : undefined}
        onMouseDown={docs ? (e) => { if (!e.target.closest?.('.dblock') && startColumnDrag(e, box.current, () => emit())) e.stopPropagation(); } : undefined}
        onMouseMove={docs ? (e) => { box.current.style.cursor = nearColumnEdge(e) ? 'col-resize' : ''; } : undefined}
        onDragOver={docs ? (e) => { if ([...(e.dataTransfer?.items || [])].some((i) => i.kind === 'file')) e.preventDefault(); } : undefined}
        onDrop={docs ? (e) => {
          if (!e.dataTransfer?.files?.length) return;
          e.preventDefault();
          const pos = document.caretRangeFromPoint?.(e.clientX, e.clientY);
          if (pos) { const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(pos); }
          keepSelection();
          insertPictures(e.dataTransfer.files);
        } : undefined}
      />
      {docs && selBlock && blockBar && (
        <div className="rt-imgbar rt-blockbar" style={{ top: Math.max(0, blockBar.top), left: blockBar.left }} role="toolbar" aria-label={t('docBlocks.block')}>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => blockAct('edit')}>✎ {t('docBlocks.edit')}</button>
          <span className="rt-imgbar-sep" />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => blockAct('up')} aria-label={t('docBlocks.up')} title={t('docBlocks.up')}>↑</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => blockAct('down')} aria-label={t('docBlocks.down')} title={t('docBlocks.down')}>↓</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => blockAct('copy')}>{t('docBlocks.duplicate')}</button>
          <button type="button" className="is-danger" onMouseDown={(e) => e.preventDefault()} onClick={() => blockAct('delete')} aria-label={t('docBlocks.remove')}>✕</button>
        </div>
      )}
      {docs && (blockEdit || importing) && (
        <Suspense fallback={null}>
          {blockEdit && <DocBlockEditor block={blockEdit.block} isNew={!blockEdit.el} onSave={saveBlock} onClose={closeBlock} />}
          {importing && <CatalogueImport lang={docLang} onClose={closeImport} onInsert={(list) => { setImporting(false); if (list.length) insertBlocks(list); }} />}
        </Suspense>
      )}
      {docs && selImg && imgBar && (
        <div className="rt-imgbar" style={{ top: Math.max(0, imgBar.top), left: imgBar.left }} role="toolbar" aria-label={t('listingForm.rt.picture')}>
          {[25, 50, 75, 100].map((w) => (
            <button key={w} type="button" className={Number(selImg.dataset.w || 100) === w ? 'on' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => imgSet((img) => { img.dataset.w = String(w); })}>{w} %</button>
          ))}
          <span className="rt-imgbar-sep" />
          {['left', 'center', 'right'].map((k) => <button key={k} type="button" title={t(`listingForm.rt.align_${k}`)} onMouseDown={(e) => e.preventDefault()} onClick={() => imgAlign(k)}>{k === 'left' ? '⇤' : k === 'right' ? '⇥' : '↔'}</button>)}
          <span className="rt-imgbar-sep" />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={imgCaption}>{t('listingForm.rt.caption')}</button>
          <button type="button" className="is-danger" onMouseDown={(e) => e.preventDefault()} onClick={imgRemove} aria-label={t('listingForm.rt.removePicture')}>✕</button>
        </div>
      )}
    </div>
  );
}
